import { ApolloError } from '@apollo/client';

interface ErrorDialogData {
  title: string;
  message: string;
  actionText?: string;
  focusField?: string | null;
  contactPhone?: string | null;
}

/**
 * Extract error code from GraphQL error extensions
 */
function getErrorCode(error: ApolloError): string | null {
  const graphQLErrors = error.graphQLErrors;
  if (!graphQLErrors || graphQLErrors.length === 0) return null;

  const extensions = graphQLErrors[0]?.extensions;
  if (!extensions) return null;

  // Check if errors array exists in extensions
  const errors = extensions.errors as any[] | undefined;
  if (!errors || errors.length === 0) return null;

  // Try to get the error code from the first error
  // ServiceDomainError sends domain, code, message, and data
  const firstError = errors[0];

  // Debug logging
  console.log('[Error Debug] Structure:', {
    domain: firstError?.domain,
    code: firstError?.code,
    message: firstError?.message,
  });

  // Check if this is a ServiceDomainError with a code field
  if (firstError?.code) {
    return String(firstError.code);
  }

  // If no code field exists, return null
  // This means the error is not a structured ServiceDomainError
  return null;
}

/**
 * Map Apollo error to user-friendly error dialog data
 */
export function mapErrorToDialog(
  error: ApolloError,
  t: (key: string, defaultValue: string) => string,
  contactPhone?: string | null
): ErrorDialogData {
  const errorCode = getErrorCode(error);

  // Map error codes to user-facing messages
  switch (errorCode) {
    case 'DELIVERY_PRICE_NOT_CALCULATED':
      return {
        title: t('online-order.error.delivery-price.title', 'Address Problem'),
        message: t(
          'online-order.error.delivery-price.message',
          'There is a problem with your delivery address. Please re-enter or double-check your address.'
        ),
        actionText: t('online-order.error.delivery-price.action', 'Check Address'),
        focusField: 'streetAddress',
        contactPhone,
      };

    case 'OUT_OF_DELIVERY_RANGE':
      return {
        title: t('online-order.error.out-of-range.title', 'Address Not Available'),
        message: t(
          'online-order.error.out-of-range.message',
          'We cannot deliver to this address. Please choose a different address or select Pickup.'
        ),
        actionText: t('online-order.error.out-of-range.action', 'Edit Address'),
        focusField: 'streetAddress',
        contactPhone,
      };

    case 'DELIVERY_ADDRESS_INCOMPLETE':
      return {
        title: t('online-order.error.incomplete-address.title', 'Missing Information'),
        message: t(
          'online-order.error.incomplete-address.message',
          'Please complete all required address fields to continue.'
        ),
        actionText: t('online-order.error.incomplete-address.action', 'Complete Address'),
        focusField: 'streetAddress',
        contactPhone,
      };

    case 'DELIVERY_TABLE_NOT_CONFIGURED':
    case 'MISSING_BRANCH_LOCATION':
      return {
        title: t('online-order.error.delivery-unavailable.title', 'Service Unavailable'),
        message: t(
          'online-order.error.delivery-unavailable.message',
          'Delivery is currently unavailable. Please select Pickup or contact the restaurant.'
        ),
        actionText: t('online-order.error.delivery-unavailable.action', 'OK'),
        focusField: null,
        contactPhone,
      };

    case 'INVALID_ORDER_STATE':
      return {
        title: t('online-order.error.invalid-state.title', 'Something Went Wrong'),
        message: t(
          'online-order.error.invalid-state.message',
          'Please refresh the page and try again.'
        ),
        actionText: t('online-order.error.invalid-state.action', 'OK'),
        focusField: null,
        contactPhone,
      };

    // Generic fallback for unknown errors
    default:
      return {
        title: t('online-order.error.generic.title', 'Unable to Continue'),
        message: t(
          'online-order.error.generic.message',
          'Something went wrong. Please try again or contact the restaurant for help.'
        ),
        actionText: t('online-order.error.generic.action', 'OK'),
        focusField: null,
        contactPhone,
      };
  }
}
